import { VAULT_NAME_MAX, VAULT_NAME_RULE, vaultNameSchema, vaultSlug } from '@nebueye/shared'
import { useQueryClient } from '@tanstack/react-query'
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { Check, Loader2 } from 'lucide-react'
import { useEffect, useState, type FormEvent } from 'react'
import { Logo } from '@/components/brand/logo'
import { Field } from '@/components/app/form'
import { Button } from '@/components/ui/button'
import { authClient, authErrorMessage } from '@/lib/auth-client'
import { useVaultAvailability } from '@/lib/vault'
import { sessionQuery, workspacesQuery } from '@/lib/session'

export const Route = createFileRoute('/onboarding')({
  beforeLoad: async ({ context }) => {
    const session = await context.queryClient.fetchQuery(sessionQuery).catch(() => null)
    if (!session) throw redirect({ to: '/sign-in' })
    if (session.user.deletionScheduledFor) throw redirect({ to: '/account-deletion' })
    const workspaces = await context.queryClient.fetchQuery(workspacesQuery)
    if (workspaces.length > 0) throw redirect({ to: '/' })
    return { session }
  },
  head: () => ({ meta: [{ title: 'Name your Vault · Nebueye' }] }),
  component: Onboarding,
})

function Onboarding() {
  const { session } = Route.useRouteContext()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const first = session.user.name.split(' ')[0] || 'My'
  const [name, setName] = useState('')
  const [error, setError] = useState<string>()
  const [pending, setPending] = useState(false)
  const availability = useVaultAvailability(name)

  useEffect(() => setError(undefined), [name])

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    const parsed = vaultNameSchema.safeParse(name)
    if (!parsed.success) return setError(parsed.error.issues[0]?.message)
    setPending(true)
    setError(undefined)
    // The server derives the slug from the name too; sending it keeps the request self-describing.
    const { error } = await authClient.organization.create({ name: parsed.data, slug: vaultSlug(parsed.data) })
    setPending(false)
    if (error) return setError(authErrorMessage(error))
    await queryClient.invalidateQueries()
    navigate({ to: '/' })
  }

  // Every error here is about the name, so it sits under the field.
  const fieldError = error ?? (availability.state === 'taken' ? availability.message : undefined)
  return (
    <div className="nebula-soft grid min-h-dvh place-items-center px-6 py-12">
      <div className="animate-in fade-in slide-in-from-bottom-2 w-full max-w-[420px] space-y-8 duration-500">
        <Logo />
        <div className="space-y-2">
          <p className="text-primary text-sm font-medium">Welcome, {first}</p>
          <h1 className="text-headline font-semibold">Name your Vault</h1>
          <p className="text-muted-foreground text-[15px] leading-6">
            A Vault holds your orders, drops and deliveries. You can invite others into it later.
          </p>
        </div>
        <form onSubmit={onSubmit} className="space-y-4">
          <Field
            label="Vault name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={VAULT_NAME_MAX + 8}
            placeholder={`${first} Vault`}
            autoFocus
            autoComplete="off"
            spellCheck={false}
            error={fieldError}
            hint={<AvailabilityHint availability={availability} />}
            aside={availability.state === 'checking' ? <Loader2 className="text-muted-foreground size-3.5 animate-spin" aria-label="Checking availability" /> : null}
          />
          <Button type="submit" size="lg" className="w-full" disabled={pending || availability.state === 'taken'}>
            {pending && <Loader2 className="animate-spin" />}
            Continue
          </Button>
        </form>
      </div>
    </div>
  )
}

function AvailabilityHint({ availability }: { availability: ReturnType<typeof useVaultAvailability> }) {
  switch (availability.state) {
    case 'free':
      return (
        <span className="text-status-success-text inline-flex items-center gap-1">
          <Check className="size-3.5" aria-hidden /> <span className="font-mono">{availability.slug}</span> is yours to take
        </span>
      )
    case 'invalid':
      return availability.message
    default:
      return VAULT_NAME_RULE
  }
}
